import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Domain } from '@monsido/modules/models/api/domain';
import { BackendDomainsRepo } from '@monsido/modules/endpoints/api/backend_admin/backend-domains.repo';
import { SessionService } from '@monsido/core/session/session.service';
import { cloneDeep } from 'lodash';
import { DomainDataType } from 'types/domain';
import { ExportProgress, CleanedDomainData } from '../types/domain-management.types';

@Injectable({
    providedIn: 'root',
})
export class DomainExportService {
    private progressSubject = new BehaviorSubject<ExportProgress>({ current: 0, total: 0, message: '' });
    public progress$ = this.progressSubject.asObservable();

    constructor (
        private backendDomainsRepo: BackendDomainsRepo,
        private sessionService: SessionService,
    ) {}

    /**
     * Export selected domains to JSON file
     */
    async exportSelectedDomains (domains: Domain[], customerName: string = 'customer'): Promise<void> {
        this.progressSubject.next({ current: 0, total: domains.length, message: 'Preparing export...' });

        const cleanedDomains = domains.map((domain, index) => {
            this.progressSubject.next({
                current: index + 1,
                total: domains.length,
                message: `Processing domain: ${domain.title || domain.url}`,
            });
            return this.cleanDomainData(domain);
        });

        const currentUser = await this.sessionService.getUser();
        const exportedBy = currentUser && typeof currentUser === 'object' && 'email' in currentUser
            ? `${currentUser.first_name} ${currentUser.last_name} (${currentUser.email})`.trim()
            : 'backend_admin';

        const exportData = {
            exportedAt: new Date().toISOString(),
            exportedBy: exportedBy,
            domainCount: domains.length,
            domainsExported: cleanedDomains.length,
            domains: cleanedDomains,
        };

        this.downloadJSON(exportData, `${customerName}_domains_selected_${this.getTimestamp()}.json`);
        this.progressSubject.next({ current: domains.length, total: domains.length, message: 'Export completed!' });
    }

    /**
     * Export all domains for a customer in batches
     */
    async exportAllDomains (customerId: number, customerName: string = 'customer'): Promise<void> {
        const allDomains: Domain[] = [];
        const pageSize = 50;
        let totalDomains = 0;

        this.progressSubject.next({ current: 0, total: 0, message: 'Fetching domain count...' });

        try {
            const firstPageParams = {
                customer_id: customerId.toString(),
                page: '1',
                per_page: pageSize.toString(),
            };

            const firstPageResponse = await this.backendDomainsRepo.getDomains(firstPageParams);

            totalDomains = (firstPageResponse as Domain[] & { total?: number }).total || firstPageResponse.length;

            if (totalDomains === 0) {
                this.progressSubject.next({ current: 0, total: 0, message: 'No domains found to export' });
                return;
            }

            this.progressSubject.next({ current: 0, total: totalDomains, message: 'Starting domain fetch...' });

            allDomains.push(...firstPageResponse);
            this.progressSubject.next({
                current: allDomains.length,
                total: totalDomains,
                message: `Fetched ${allDomains.length} of ${totalDomains} domains...`,
            });

            const actualPageSize = firstPageResponse.length;
            const totalPages = Math.ceil(totalDomains / actualPageSize);

            for (let page = 2; page <= totalPages; page++) {
                const params = {
                    customer_id: customerId.toString(),
                    page: page.toString(),
                    per_page: pageSize.toString(),
                };

                const domains = await this.backendDomainsRepo.getDomains(params);
                allDomains.push(...domains);

                this.progressSubject.next({
                    current: allDomains.length,
                    total: totalDomains,
                    message: `Fetched ${allDomains.length} of ${totalDomains} domains... (Page ${page}/${totalPages})`,
                });
            }

            this.progressSubject.next({
                current: allDomains.length,
                total: allDomains.length,
                message: 'Processing domains for export...',
            });

            const cleanedDomains = allDomains.map(domain => this.cleanDomainData(domain));

            const currentUser = await this.sessionService.getUser();
            const exportedBy = currentUser && typeof currentUser === 'object' && 'email' in currentUser
                ? `${currentUser.first_name} ${currentUser.last_name} (${currentUser.email})`.trim()
                : 'backend_admin';

            const exportData = {
                exportedAt: new Date().toISOString(),
                exportedBy: exportedBy,
                domainCount: totalDomains,
                domainsExported: cleanedDomains.length,
                domains: cleanedDomains,
            };

            this.downloadJSON(exportData, `${customerName}_domains_all_${this.getTimestamp()}.json`);
            this.progressSubject.next({
                current: allDomains.length,
                total: allDomains.length,
                message: `Export completed! ${allDomains.length} domains exported.`,
            });

        } catch (error) {
            this.progressSubject.next({
                current: 0,
                total: 0,
                message: `Export failed: ${error?.message || 'Unknown error'}`,
            });
            throw error;
        }
    }

    /**
     * Clean domain data by removing customer-specific and runtime data
     */
    private cleanDomainData (domain: Domain): CleanedDomainData {
        const cleaned = cloneDeep(domain) as DomainDataType;

        delete cleaned.customer_id;
        delete cleaned.customer;

        delete cleaned.crawl_history;
        delete cleaned.crawl_status;
        delete cleaned.last_scan;
        delete cleaned.crawled_pages;
        delete cleaned.token;
        delete cleaned.running_groups;

        if (cleaned.path_constraints) {
            cleaned.path_constraints = cleaned.path_constraints.map(constraint => {
                const cleanConstraint = cloneDeep(constraint);
                delete cleanConstraint.id;
                delete cleanConstraint.created_at;
                delete cleanConstraint.updated_at;
                return cleanConstraint;
            });
        }

        if (cleaned.link_excludes) {
            cleaned.link_excludes = cleaned.link_excludes.map(exclude => {
                const cleanExclude = cloneDeep(exclude);
                delete cleanExclude.id;
                delete cleanExclude.created_at;
                delete cleanExclude.updated_at;
                return cleanExclude;
            });
        }

        delete cleaned.domain_users;

        if (cleaned.domain_groups) {
            cleaned.domain_groups = cleaned.domain_groups.map(group => {
                const cleanGroup = cloneDeep(group);
                delete cleanGroup.id;
                delete cleanGroup.created_at;
                delete cleanGroup.updated_at;
                if (cleanGroup.domain_group_members) {
                    cleanGroup.domain_group_members = cleanGroup.domain_group_members.map(member => {
                        const cleanMember = cloneDeep(member) as Record<string, unknown>;
                        delete cleanMember.id;
                        delete cleanMember.domain_group_id;
                        delete cleanMember.user_id;
                        return cleanMember;
                    });
                }
                return cleanGroup;
            });
        }

        delete cleaned.created_at;
        delete cleaned.updated_at;
        delete cleaned.id;

        return cleaned as CleanedDomainData;
    }

    /**
     * Generate timestamp for filename
     */
    private getTimestamp (): string {
        const now = new Date();
        return now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    }

    /**
     * Download data as JSON file
     */
    private downloadJSON (data: Record<string, unknown>, filename: string): void {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
    }

    /**
     * Reset progress state
     */
    resetProgress (): void {
        this.progressSubject.next({ current: 0, total: 0, message: '' });
    }
}
